import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, lastValueFrom, Subscription } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter, startWith } from 'rxjs/operators';
import { UserProfileService } from 'src/app/core/services/user.service';
import { LayoutService } from 'src/app/core/services/layout.service';
import { AppState } from 'src/app/store';
import { getCurrentTheme } from 'src/app/store/user-preferences/user-preference.selectors';
import { DARK_THEME, LIGHT_THEME } from 'src/app/store/user-preferences/user-preference.state';
import { getKey } from 'src/app/core/utils/functions';
import { REDIRECT_URLS, TRIAL_EVENT_TYPES } from 'src/app/core/data/constants';
import { LogService } from 'src/app/core/services/log.service';
import { ConfirmationModalComponent, ConfirmationModalConfig } from 'src/app/shared/ui/confirmation-modal/confirmation-modal.component';
import { ToastrService } from 'ngx-toastr';
import { TrialService } from 'src/app/core/services/trial.service';
import { HttpStatusCode } from '@angular/common/http';
import { BannerInfo } from 'src/app/core/models/user.models';

@Component({
    selector: 'app-detached-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {

  isInitCompleted: boolean;
  themeSub: Subscription;
  showTrialBanner = false;
  private routerSub: Subscription;
  private userLoadedSub: Subscription;
  @ViewChild('confirmationModalRef') confirmationModal: ConfirmationModalComponent;
  private pendingConfirmationType: TRIAL_EVENT_TYPES | null = null;
  private isCtaLoading = false;
  private readonly bannerInfoSubject = new BehaviorSubject<BannerInfo | null | undefined>(null);
  bannerInfo$ = this.bannerInfoSubject.asObservable();

  constructor(
    public userService: UserProfileService,
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly layoutService: LayoutService,
    private readonly logService: LogService,
    private readonly toastr: ToastrService,
    private readonly trialService: TrialService,
  ) { }

  ngOnInit() {
    this.isInitCompleted = false;
    this.setTheme();
    this.updateTrialBannerVisibility();
    this.routerSub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null)
    ).subscribe(() => this.updateTrialBannerVisibility());
    this.userLoadedSub = this.userService.userLoaded$.subscribe(() => this.updateTrialBannerVisibility());
  }

  ngAfterViewInit() {
    document.body.removeAttribute('data-layout-mode');
    document.body.setAttribute('data-layout', 'detached');
    this.isInitCompleted = true;
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.userLoadedSub?.unsubscribe();
  }

  /**
   * Show trial banner on Core and Premium Analytics for users with bannerInfo.
   */
  private updateTrialBannerVisibility(): void {
    const dashboardKey = getKey(this.router.url);
    const isAnalyticsDashboard = ['analytics', 'stroke-dashboard'].includes(dashboardKey);
    const hasBannerInfo = !!this.userService.USER_PROFILE?.uiElements?.bannerInfo;
    this.showTrialBanner = isAnalyticsDashboard && hasBannerInfo;
    this.layoutService.setStickyHeaderHeight(this.showTrialBanner);
    this.updateBannerInfo();
  }

  /**
   * It sets the subscription to apply theme of the application.
   */
  setTheme() {
    this.themeSub = this.store.select(getCurrentTheme).subscribe({
      next: theme => {
        if(theme === DARK_THEME) {
          document.documentElement.setAttribute("data-theme", DARK_THEME);
        } else {
          document.documentElement.setAttribute("data-theme", LIGHT_THEME);
        }
      }
    })
  }

  /**
   * To activate the trial and redirect to the respective page.
   * @param event - The event object containing the event type.
   */
  onBannerCTAClick(event: { eventType?: TRIAL_EVENT_TYPES }) {
    try {
      let config: ConfirmationModalConfig | null = null;
      switch (event?.eventType) {
        case TRIAL_EVENT_TYPES.TRIAL_STARTED:
          this.pendingConfirmationType = TRIAL_EVENT_TYPES.TRIAL_STARTED;
          config = {
            title: 'Start Premium Analytics trial',
            checkboxDescription: ['Do you want to start the free 60 day trial of Premium Analytics?'],
            confirmLabel: 'Yes',
            cancelLabel: 'No',
            requireCheckbox: true
          };
          break;
        case TRIAL_EVENT_TYPES.UPGRADE_INTENT:
          this.pendingConfirmationType = TRIAL_EVENT_TYPES.UPGRADE_INTENT;
          config = {
            title: 'Upgrade to Premium Analytics',
            checkboxDescription: ['Do you want to proceed with "Upgrade to Premium"?', 'Please click "Yes" and Sevaro team will reach out to you with next steps.'],
            confirmLabel: 'Yes',
            cancelLabel: 'No',
            requireCheckbox: true
          };
          break;
        default:
          this.pendingConfirmationType = null;
          break;
      }
      if (config) {
        this.confirmationModal?.open(config);
      }
    } catch (e) {
      this.logService.error(e);
    }
  }

  /**
   * To activate the trial and redirect to the respective page.
   */
  async onStartTrialClick() {
    this.setCtaLoading(true);
    try {
      this.logService.info('Triggering trial activation');
      this.userService?.trialActivation$?.next(true);
      const response = await lastValueFrom(this.trialService.activateTrial());
      if (response?.status === HttpStatusCode.Created) {
        this.userService?.trialActivation$?.next(false);
        globalThis.location.href = REDIRECT_URLS.premiumUser;
        this.logService.info('Trial activated successfully and redirecting to premium analytics');
      }
    } catch (e) {
      this.logService.error(e);
      this.userService?.trialActivation$?.next(false);
    } finally {
      this.setCtaLoading(false);
    }
  }

  /**
   * To trigger the upgrade intent.
   */
  async onUpgradeIntentClick() {
    this.setCtaLoading(true);
    try {
      this.logService.info('Triggering upgrade intent');
      const response = await lastValueFrom(this.trialService.upgradeIntent());
      if (response?.status === HttpStatusCode.Accepted) {
        if (this.userService.USER_PROFILE?.uiElements) {
          this.userService.USER_PROFILE.uiElements.bannerInfo = {
            ...this.userService.USER_PROFILE.uiElements.bannerInfo,
            cta: response?.data?.cta
          } as BannerInfo;
          this.updateBannerInfo();
        }
        this.showUpgradeIntentNotification();
      }
    } catch (e) {
      this.logService.error(e);
    } finally {
      this.setCtaLoading(false);
    }
  }

  /**
   * To handle the trial confirmation modal.
   * @param event - The event object containing the confirmed status.
   */
  async handleTrialConfirmation(event: { confirmed: boolean }){
    if (!event.confirmed || !this.pendingConfirmationType) {
      this.pendingConfirmationType = null;
      return;
    }
    switch (this.pendingConfirmationType) {
      case TRIAL_EVENT_TYPES.TRIAL_STARTED:
        await this.onStartTrialClick();
        break;
      case TRIAL_EVENT_TYPES.UPGRADE_INTENT:
        await this.onUpgradeIntentClick();
        break;
    }
    this.pendingConfirmationType = null;
  }

  /**
   * To show the upgrade intent confirmation toast.
   */
  private showUpgradeIntentNotification() {
    this.toastr.success('Thanks for your interest to Upgrade to Premium. Sevaro team will reach out to you.');
  }

  /**
   * It updates the banner info.
   * @returns It updates the banner info.
   */
  private updateBannerInfo(): void {
    const info = this.userService.USER_PROFILE?.uiElements?.bannerInfo;
    if (!info?.cta) {
      this.bannerInfoSubject.next(info);
      return;
    }
    this.bannerInfoSubject.next({
      ...info,
      cta: {
        ...info.cta,
        disabled: !!info.cta.disabled || this.isCtaLoading
      }
    });
  }

  /**
   * It sets the loading state of the CTA button.
   * @param isLoading - The loading state of the CTA button.
   */
  private setCtaLoading(isLoading: boolean): void {
    if (this.isCtaLoading === isLoading) {
      return;
    }
    this.isCtaLoading = isLoading;
    this.updateBannerInfo();
  }
}
